import React, { useCallback, useEffect, useState } from "react";
import { FaUtensils, FaListAlt, FaStar, FaSignInAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { DEFAULT_SITE_CONTENT, getSiteContent } from "../firebase";
import "./MainPage.css";

const COOKIE_CONSENT_KEY = "cookieConsentAcceptedAt";
const COOKIE_CONSENT_DURATION_MS = 60 * 24 * 60 * 60 * 1000;

function MainPage() {
    const [index, setIndex] = useState(0);
    const [showCookiePopup, setShowCookiePopup] = useState(false);
    const [siteContent, setSiteContent] = useState(DEFAULT_SITE_CONTENT);

    const moveSlide = useCallback((direction) => {
        setIndex((current) => {
            const nextIndex = (current + direction + 10) % 10;
            const track = document.getElementById("sliderTrack");

            if (track) {
                const img = track.querySelector("img");
                if (img) {
                    const imgWidth = img.clientWidth;
                    track.style.transform = `translateX(-${nextIndex * imgWidth}px)`;
                }
            }

            return nextIndex;
        });
    }, []);

    useEffect(() => {
        let active = true;

        async function loadContent() {
            try {
                const content = await getSiteContent();
                if (active) {
                    setSiteContent({ ...DEFAULT_SITE_CONTENT, ...content });
                }
            } catch (error) {
                console.error("Unable to load website content from Firestore:", error);
            }
        }

        loadContent();

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            moveSlide(1);
        }, 5000);

        return () => clearInterval(interval);
    }, [moveSlide]);

    useEffect(() => {
        const track = document.getElementById("sliderTrack");

        if (!track) {
            return;
        }

        const img = track.querySelector("img");
        if (!img) {
            return;
        }

        track.style.transform = `translateX(-${index * img.clientWidth}px)`;
    }, [index]);

    useEffect(() => {
        const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);

        if (!savedConsent) {
            setShowCookiePopup(true);
            return;
        }

        const acceptedAt = Number(savedConsent);
        const isExpired = Number.isNaN(acceptedAt)
            || Date.now() - acceptedAt > COOKIE_CONSENT_DURATION_MS;

        setShowCookiePopup(isExpired);
    }, []);

    const handleAcceptCookies = () => {
        localStorage.setItem(COOKIE_CONSENT_KEY, String(Date.now()));
        setShowCookiePopup(false);
    };

    const handleDeclineCookies = () => {
        localStorage.setItem(COOKIE_CONSENT_KEY, String(Date.now()));
        setShowCookiePopup(false);
    };

    const menuImageSource = siteContent.menuImageDataUrl || "/img/menu23092026.jpg";

    return (
        <>
            <svg width="0" height="0">
                <defs>
                    <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#FFD700" />
                        <stop offset="100%" stopColor="#FFA500" />
                    </linearGradient>
                </defs>
            </svg>

            <div className="navbar">
                <div className="navbar-left">
                    🔥 <span className="neon-wrapper"><span className="neon-red">DÖNER</span></span> – Unterliederbach
                </div>

                <div className="navbar-right">
                    <a href="#menu" className="nav-icon" aria-label="Menu"><FaUtensils /></a>
                    <a href="#gallery" className="nav-icon" aria-label="Offer"><FaListAlt /></a>
                    <a href="#bewertungen" className="nav-icon" aria-label="Reviews"><FaStar /></a>
                    <Link to="/login" className="nav-icon" aria-label="Login"><FaSignInAlt /></Link>
                </div>
            </div>

            <section id="menu" className="menu-section">
                <h2>{siteContent.menuSectionTitle}</h2>
                <div className="menu-line"></div>
                <p>{siteContent.menuSectionSubtitle}</p>
            </section>

            <div className="hero-image">
                <div className="hero-text">
                    <h1>{siteContent.heroTitle}</h1>
                    <p>{siteContent.heroSubtitle}</p>
                </div>
            </div>

            <div className="row-sections">
                <section className="menu-section">
                    <h2>{siteContent.menuTitle}</h2>
                    <p>{siteContent.menuDescription}</p>
                    <div className="menu-showcase-image-wrap">
                        <img src={menuImageSource} alt="Menü im Imbiss Insel" className="menu-showcase-image" />
                    </div>
                </section>

                <section className="map-section">
                    <h2>{siteContent.mapTitle}</h2>
                    <p>{siteContent.mapDescription}</p>

                    <iframe
                        title="Map of Imbiss Insel location"
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5117.5881363269855!2d8.524991683423597!3d50.10886139981492!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47bda0cf4ce167cb%3A0xaf7f4bfeae315b00!2sFrankfurt%20(Main)%20Unterliederbach%20Bahnhof!5e0!3m2!1sde!2sde!4v1783800234661!5m2!1sde!2sde"
                        width="600"
                        height="450"
                        style={{ border: 0 }}
                        allowFullScreen={true}
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                    ></iframe>
                </section>

                <section className="adv-section">
                    <h2>Advertising</h2>
                    <p>Hier kannst du Werbung schalten.</p>
                </section>

                <section className="auto-slider-section" id="gallery">
                    <h2>{siteContent.galleryTitle}</h2>

                    <div className="auto-slider-wrapper">
                        <button type="button" className="arrow left" onClick={() => moveSlide(-1)}>❮</button>

                        <div className="auto-slider">
                            <div className="auto-slider-track" id="sliderTrack">
                                <img src="/img/foto1.jpg" alt="Slider 1" />
                                <img src="/img/foto2.jpg" alt="Slider 2" />
                                <img src="/img/foto3.jpg" alt="Slider 3" />
                                <img src="/img/foto4.jpg" alt="Slider 4" />
                                <img src="/img/foto5.jpg" alt="Slider 5" />
                                <img src="/img/foto6.jpg" alt="Slider 6" />
                                <img src="/img/foto7.jpg" alt="Slider 7" />
                                <img src="/img/foto8.jpg" alt="Slider 8" />
                                <img src="/img/foto9.jpg" alt="Slider 9" />
                                <img src="/img/foto10.jpg" alt="Slider 10" />
                            </div>
                        </div>

                        <button type="button" className="arrow right" onClick={() => moveSlide(1)}>❯</button>
                    </div>
                </section>
            </div>

            <section id="bewertungen" className="bewertung-section">
                <h2>Bewertungen unserer Kunden</h2>

                <div className="review">
                    <p>„Bester Döner in Frankfurt! Frisch und super lecker.“ – Ümit</p>
                    <div className="stars">
                        <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                    </div>
                </div>

                <div className="review">
                    <p>„Sehr freundliches Team und schnelle Bedienung.“ – Ayhan</p>
                    <div className="stars">
                        <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                    </div>
                </div>

                <div className="review">
                    <p>„Der Dürüm ist ein Traum. Immer wieder gerne!“ – Tobi</p>
                    <div className="stars">
                        <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                    </div>
                </div>
            </section>

            <footer className="footer">
                <p>© 2026 FreeLancer 7776.link</p>
            </footer>

            {showCookiePopup && (
                <div className="cookie-popup" role="dialog" aria-live="polite" aria-label="Cookie Hinweis">
                    <p>
                        Wir verwenden Cookies, um die Nutzung der Website zu verbessern.
                        Du kannst Cookies akzeptieren oder ablehnen.
                    </p>
                    <div className="cookie-popup-actions">
                        <button type="button" className="decline-btn" onClick={handleDeclineCookies}>Ablehnen</button>
                        <button type="button" onClick={handleAcceptCookies}>Akzeptieren</button>
                    </div>
                </div>
            )}
        </>
    );
}

export default MainPage;
